import type { Route } from 'vue-router';

export const TitlePlayerQueryParamMapping = {
	lcId: 'linkContextId',
	expPC: 'expPromoCode',
	play: 'titleSlug',
	isFb: 'isFallback',
	package: 'packageShortName',
} as const;

type TitlePlayerQueryParamMapping = typeof TitlePlayerQueryParamMapping;
export type QueryParamKey = keyof TitlePlayerQueryParamMapping;
type QueryParamValue = TitlePlayerQueryParamMapping[QueryParamKey];

export type TitlePlayerQueryParams = {
	[K in QueryParamValue]?: K extends 'isFallback' ? boolean : string;
};

/**
 * Builds a title player URL with the required `package=jwt` query param and optional tracking params.
 *
 * @param options.fullPath - The title's full path (e.g. `/us/movie/inception`). Returns `null` if empty.
 * @param options.fullUrl - When `true`, prepends the absolute base URL (domain or localhost). Defaults to relative path.
 */
export function getTitlePlayerUrl(
	options: { fullPath: string; fullUrl?: boolean } & TitlePlayerQueryParams
): string | null {
	const { fullPath, linkContextId, expPromoCode, isFallback, fullUrl, packageShortName = 'jwt' } = options;

	if (!fullPath) {
		return null;
	}
	const params = new URLSearchParams({ package: packageShortName });
	if (linkContextId) params.set('lcId', linkContextId);
	if (expPromoCode) params.set('expPC', expPromoCode);
	if (isFallback) params.set('isFb', '1');
	const queryParams = `?${params.toString()}`;
	let baseUrl = `https://www.${JW_CONFIG.DOMAIN}`;
	if (process.client && window.location.hostname === 'localhost') {
		baseUrl = `http://localhost:8080`;
	}
	if (fullUrl) {
		return `${baseUrl}${fullPath}${queryParams}`;
	}

	return `${fullPath}${queryParams}`;
}

/**
 * Parses a Vue Router query object into typed title player query params using `TitlePlayerQueryParamMapping`.
 * Non-string values (arrays, null, undefined) are skipped.
 *
 * @param routerQuery - The raw query object from `route.query`.
 * @returns Typed `TitlePlayerQueryParams` with only the recognized keys populated.
 */
export function getTitlePlayerQueryParams(routerQuery: Route['query']): TitlePlayerQueryParams {
	const params: TitlePlayerQueryParams = {};

	Object.entries(TitlePlayerQueryParamMapping).forEach(([queryKey, paramKey]) => {
		const value = routerQuery[queryKey];
		if (typeof value !== 'string') return;

		if (paramKey === 'isFallback') {
			params[paramKey] = value === '1';
		} else {
			params[paramKey] = value;
		}
	});

	return params;
}
